import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Feather, Loader2 } from "lucide-react";

const FREQS = ["Recalibrating", "Expanding", "Grieving", "Rising", "Stillness", "Fire"];

export default function Journal() {
  const { user } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [reflection, setReflection] = useState("");
  const [freq, setFreq] = useState("Recalibrating");
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/journal/prompt")
      .then((r) => setPrompt(r.data.prompt))
      .catch((err) => console.error("Failed to load prompt:", err));
    if (user) {
      api
        .get("/journal")
        .then((r) => setEntries(r.data.entries))
        .catch((err) => console.error("Failed to load entries:", err));
    }
  }, [user]);

  const save = async () => {
    if (reflection.trim().length < 4) {
      toast.error("Write at least a few words.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/journal", { prompt, reflection, frequency: freq });
      setEntries((e) => [r.data, ...e]);
      setReflection("");
      toast.success("Entry saved.");
    } catch {
      toast.error("Couldn't save entry.");
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="pt-20 sm:pt-24 min-h-screen" data-testid="journal-page">
        <div className="mx-auto max-w-2xl px-5 sm:px-6 py-16 sm:py-24 text-center">
          <Feather className="h-8 w-8 text-accent mx-auto mb-6" />
          <div className="eyebrow mb-4">The Shift Journal</div>
          <h1 className="font-serif text-3xl sm:text-5xl leading-tight mb-5 sm:mb-6">
            Sign in to keep your reflections.
          </h1>
          <p className="text-muted-foreground mb-8 leading-relaxed text-sm sm:text-base">
            Your journal is a private mirror. Create an account to save your entries and track your frequency over time.
          </p>
          <Link to="/auth" className="block sm:inline-block">
            <Button className="w-full sm:w-auto btn-pill font-mono uppercase text-xs tracking-widest h-12 px-6" data-testid="journal-signin-btn">
              Sign in / Create account
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 sm:pt-24" data-testid="journal-page">
      <section className="mx-auto max-w-4xl px-5 sm:px-6 py-10 sm:py-16">
        <div className="eyebrow mb-4">Today's prompt</div>
        <motion.h1
          key={prompt}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-serif text-2xl sm:text-5xl leading-snug sm:leading-tight mb-8 sm:mb-10"
          data-testid="journal-prompt"
        >
          {prompt || <Loader2 className="h-6 w-6 animate-spin" />}
        </motion.h1>

        <div className="border border-border p-5 sm:p-8 bg-secondary/20">
          <Textarea
            value={reflection}
            onChange={(e) => setReflection(e.target.value)}
            rows={6}
            placeholder="What's asking to be seen right now..."
            className="border-0 bg-transparent focus-visible:ring-0 font-serif text-base sm:text-lg resize-none p-0 min-h-[140px]"
            data-testid="journal-textarea"
          />
          <div className="mt-5 sm:mt-6 flex flex-col gap-4">
            <div className="flex flex-wrap gap-2">
              {FREQS.map((f) => (
                <button
                  key={f}
                  onClick={() => setFreq(f)}
                  className={`px-3 h-9 flex items-center font-mono text-[10px] tracking-widest uppercase border transition-colors ${
                    freq === f ? "border-accent text-accent bg-accent/10" : "border-border text-muted-foreground"
                  }`}
                  data-testid={`journal-freq-${f.toLowerCase()}`}
                >
                  {f}
                </button>
              ))}
            </div>
            <Button
              onClick={save}
              disabled={saving}
              className="w-full sm:w-auto sm:self-end btn-pill font-mono uppercase text-xs tracking-widest h-12 px-6"
              data-testid="journal-save-btn"
            >
              {saving ? "Saving..." : "Save entry"}
            </Button>
          </div>
        </div>

        {entries.length > 0 && (
          <div className="mt-12 sm:mt-16">
            <div className="eyebrow mb-6">Your entries</div>
            <div className="space-y-4">
              {entries.map((e) => (
                <div key={e.id} className="border border-border p-5 sm:p-6" data-testid={`journal-entry-${e.id}`}>
                  <div className="flex items-center justify-between mb-3 gap-3">
                    <span className="font-mono text-[10px] tracking-widest uppercase text-accent">
                      {e.frequency}
                    </span>
                    <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">
                      {new Date(e.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground italic mb-3">{e.prompt}</p>
                  <p className="font-serif text-base sm:text-lg leading-relaxed">{e.reflection}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
